<!--
  ConnectionTypePicker.vue

  Step 1 of "Add Account" when an integration exposes more than one auth method
  (e.g. OAuth and API Key). Lists the available connection options; selecting one
  emits `select` so the caller can continue with that option's flow.
-->
<template>
  <div class="connection-type-list">
    <button
      v-for="option in options"
      :key="option.key ?? option.type"
      class="connection-type-row"
      type="button"
      @click="$emit('select', option)"
    >
      <span class="connection-type-text">{{ labelFor(option) }}</span>
      <ChevronRightIcon class="connection-type-chevron" />
    </button>
  </div>
</template>

<script lang="ts">
import { match } from "ts-pattern";
import { defineComponent, PropType } from "vue";

import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import type { ConnectionOption } from "@/components/settings/integrations/useMembraneSetup";

export const connectionOptionLabel = (option: ConnectionOption): string => {
  if (option.title && option.title.length > 0) return option.title;
  // Membrane's option `type` is one of a fixed set (oauth2, oauth1,
  // client-credentials, proxy, membrane-token, integration-app-token). Map the
  // OAuth variants to a friendly label and humanize the rest (e.g.
  // "client-credentials" → "Client Credentials") rather than showing the raw
  // slug.
  return match(option.type)
    .with("oauth2", "oauth1", () => "OAuth")
    .otherwise((type) =>
      type
        ? type
            .split(/[-_\s]+/)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ")
        : "Connection"
    );
};

export default defineComponent({
  components: { ChevronRightIcon },
  props: {
    options: {
      type: Array as PropType<ConnectionOption[]>,
      required: true,
    },
  },
  emits: {
    select: (_option: ConnectionOption) => true,
  },
  setup() {
    return { labelFor: connectionOptionLabel };
  },
});
</script>

<style scoped lang="scss">
.connection-type-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.connection-type-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 14px;
  border-radius: $border-radius-m;
  background: none;
  cursor: pointer;
  text-align: start;
  @include transition(background-color 0.15s ease-out);
  @include themed() {
    border: 1px solid t($grey-15);
    color: t($primary);
  }

  &:hover {
    @include themed() {
      background-color: t($grey-05);
      border-color: t($grey-30);
    }
  }
}

.connection-type-text {
  @extend %font-body-medium;
}

.connection-type-chevron {
  width: 14px;
  height: 14px;
  @include themed() {
    color: t($grey-40);
  }
}
</style>
